import { db } from '../firebase/firebase';
import { 
  doc, 
  setDoc, 
  getDoc, 
  addDoc,
  collection,
  serverTimestamp 
} from 'firebase/firestore';

// User authentication service
export const userService = {
  // Cache for user data
  _userCache: new Map(),
  _cacheTimeout: 24 * 60 * 60 * 1000, // 24 hours cache
  // Create or update user document in Firestore
  async createOrUpdateUser(user) {
    try {
      const userRef = doc(db, 'Users', user.uid);
      const userDoc = await getDoc(userRef);
      
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        lastLogin: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (!userDoc.exists()) {
        // New user - add creation timestamp
        userData.createdAt = serverTimestamp();
        userData.isActive = true;
        console.log('Creating new user document:', userData);
      } else {
        console.log('Updating existing user document:', userData);
      }

      await setDoc(userRef, userData, { merge: true });
      return { success: true, isNewUser: !userDoc.exists() };
    } catch (error) {
      console.error('Error creating/updating user:', error);
      return { success: false, error: error.message };
    }
  },

  // Get user document with caching
  async getUserWithCache(uid, forceRefresh = false) {
    const now = Date.now();
    const cacheKey = uid;
    const cached = this._userCache.get(cacheKey);
    
    // Return cached data if it exists, is not expired, and we're not forcing refresh
    if (!forceRefresh && cached && (now - cached.timestamp) < this._cacheTimeout) {
      console.log('Returning cached user data for:', uid);
      return { success: true, user: cached.data, fromCache: true };
    }
    
    // Fetch fresh data
    console.log('Fetching fresh user data for:', uid);
    const result = await this.getUser(uid);
    
    if (result.success && result.user) {
      // Cache the result
      this._userCache.set(cacheKey, {
        data: result.user,
        timestamp: now
      });
    }
    
    return { ...result, fromCache: false };
  },

  // Clear user cache (useful when payment status changes)
  clearUserCache(uid) {
    if (uid) {
      this._userCache.delete(uid);
    } else {
      this._userCache.clear();
    }
  },

  // Get user document
  async getUser(uid) {
    try {
      const userRef = doc(db, 'Users', uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        return { success: true, user: userDoc.data() };
      } else {
        return { success: true, user: null };
      }
    } catch (error) {
      console.error('Error getting user:', error);
      return { success: false, error: error.message };
    }
  },

  // Log user login activity
  async logUserActivity(userId, email, action) {
    try {
      const activityRef = collection(db, 'userActivity');
      await addDoc(activityRef, {
        userId: userId,
        email: email,
        action: action,
        timestamp: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error logging user activity:', error);
      return { success: false, error: error.message };
    }
  }
};